import { Link, useNavigate } from 'react-router-dom';
import { Settings, Network, BookOpen, Sparkles, Zap } from 'lucide-react';
import { useState } from 'react';
import { PRIMITIVE_METADATA, PRIMITIVE_CATEGORIES, PrimitiveCard } from '@unwritten/ui-domains';

export function ConfigsMain() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [effectVariant, setEffectVariant] = useState<'glowing' | 'glare' | 'none'>('glowing');
  
  const filteredPrimitives = Object.values(PRIMITIVE_METADATA).filter((primitive) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      primitive.name.toLowerCase().includes(searchLower) ||
      primitive.description.toLowerCase().includes(searchLower) ||
      primitive.tags.some((tag) => tag.toLowerCase().includes(searchLower))
    );
  });

  const primitivesByCategory = Object.entries(PRIMITIVE_CATEGORIES).map(
    ([key, category]) => ({
      ...category,
      key,
      primitives: category.primitives
        .map((id) => PRIMITIVE_METADATA[id])
        .filter((p) => filteredPrimitives.includes(p)),
    })
  );

  const totalPrimitives = Object.keys(PRIMITIVE_METADATA).length;
  const withDatabase = Object.values(PRIMITIVE_METADATA).filter((p) => p.hasDatabase).length;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Settings className="w-8 h-8 text-uw-brand-primary" />
          <div>
            <h1 className="text-4xl font-bold text-uw-text-primary">Game Primitives</h1>
            <p className="text-sm text-uw-text-secondary mt-1">
              Core game domain primitives with type-safe schemas and services
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <Link
            to="/graph"
            className="inline-flex items-center px-4 py-2 bg-uw-brand-secondary text-white rounded-md hover:bg-uw-brand-secondary/90 transition-colors"
          >
            <Network className="w-4 h-4 mr-2" />
            Explore Graph
          </Link>
          <a
            href="https://github.com/unwritten/packages/game-primitives"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 bg-uw-interactive-secondary text-white rounded-md hover:bg-uw-interactive-secondary-hover transition-colors"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Documentation
          </a>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-uw-surface-default rounded-lg shadow p-4 border border-uw-border-default">
          <div className="text-sm text-uw-text-secondary">Total Primitives</div>
          <div className="text-2xl font-bold text-uw-text-primary">{totalPrimitives}</div>
        </div>
        <div className="bg-uw-surface-default rounded-lg shadow p-4 border border-uw-border-default">
          <div className="text-sm text-uw-text-secondary">With Database</div>
          <div className="text-2xl font-bold text-uw-text-primary">{withDatabase}</div>
        </div>
        <div className="bg-uw-surface-default rounded-lg shadow p-4 border border-uw-border-default">
          <div className="text-sm text-uw-text-secondary">Schema Version</div>
          <div className="text-2xl font-bold text-uw-text-primary">1.0.0</div>
        </div>
      </div>

      <div className="mb-6 space-y-4">
        <input
          type="text"
          placeholder="Search primitives by name, description, or tags..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-3 border border-uw-border-default rounded-lg bg-uw-bg-secondary text-uw-text-primary placeholder:text-uw-text-tertiary focus:outline-none focus:ring-2 focus:ring-uw-interactive-primary"
        />

        <div className="flex items-center gap-3">
          <span className="text-sm text-uw-text-secondary">Card Effect:</span>
          <div className="flex gap-2">
            <button
              onClick={() => setEffectVariant('glowing')}
              className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                effectVariant === 'glowing'
                  ? 'bg-uw-brand-primary text-white'
                  : 'bg-uw-surface-elevated text-uw-text-secondary hover:bg-uw-surface-elevated hover:text-uw-text-primary'
              }`}
            >
              <Sparkles className="w-4 h-4 mr-1.5" />
              Glowing
            </button>
            <button
              onClick={() => setEffectVariant('glare')}
              className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                effectVariant === 'glare'
                  ? 'bg-uw-brand-primary text-white'
                  : 'bg-uw-surface-elevated text-uw-text-secondary hover:bg-uw-surface-elevated hover:text-uw-text-primary'
              }`}
            >
              <Zap className="w-4 h-4 mr-1.5" />
              Holographic
            </button>
            <button
              onClick={() => setEffectVariant('none')}
              className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                effectVariant === 'none'
                  ? 'bg-uw-brand-primary text-white'
                  : 'bg-uw-surface-elevated text-uw-text-secondary hover:bg-uw-surface-elevated hover:text-uw-text-primary'
              }`}
            >
              None
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-8">
        {primitivesByCategory.map((category) => {
          if (category.primitives.length === 0) return null;
          
          return (
            <div key={category.key}>
              <div className="flex items-center gap-2 mb-4">
                <div
                  className="w-1 h-6 rounded"
                  style={{ backgroundColor: `var(--uw-category-${category.key})` }}
                />
                <h2 className="text-2xl font-semibold text-uw-text-primary">
                  {category.name}
                </h2>
                <span className="text-sm text-uw-text-tertiary">
                  ({category.primitives.length})
                </span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.primitives.map((primitive) => (
                  <PrimitiveCard
                    key={primitive.id}
                    primitive={primitive}
                    recordCount={undefined}
                    effectVariant={effectVariant}
                    onNavigate={(id) => navigate(`/configs/${id}`)}
                  />
                ))}
              </div>
            </div>
          );
        })}
        
        {filteredPrimitives.length === 0 && (
          <div className="text-center py-12 text-uw-text-tertiary">
            No primitives found matching &quot;{searchTerm}&quot;
          </div>
        )}
      </div>
    </div>
  );
}
